import type { ComponentType } from 'react'
import AIIcon from './AIIcon'
import AnalyticsIcon from './AnalyticsIcon'
import AndroidIcon from './AndroidIcon'
import AppStoreIcon from './AppStoreIcon'
import CloudIcon from './CloudIcon'
import ConsultingIcon from './ConsultingIcon'
import CrmIcon from './CrmIcon'
import DevelopmentIcon from './DevelopmentIcon'
import FirewallIcon from './FirewallIcon'
import KubernetesLogo from './KubernetesLogo'
import LightbulbIcon from './LightbulbIcon'
import MaleAvatarIcon from './MaleAvatarIcon'
import PartnershipIcon from './PartnershipIcon'
import ProcessIcon from './ProcessIcon'
import StartupIcon from './StartupIcon'
import SupportIcon from './SupportIcon'
import TargetIcon from './TargetIcon'
import Web3Icon from './Web3Icon'
import WebDevelopmentIcon from './WebDevelopmentIcon'

import { createLottieIcon } from './LottieIcon'
import workflowAnimation from '../../assets/animations/workflow.json'
import dataTransferAnimation from '../../assets/animations/data-transfer.json'
import monitoringAnimation from '../../assets/animations/monitoring.json'
import roadmapAnimation from '../../assets/animations/roadmap.json'
import blueprintAnimation from '../../assets/animations/blueprint.json'
import cartAnimation from '../../assets/animations/cart.json'
import optimizationAnimation from '../../assets/animations/optimization.json'
import saasAnimation from '../../assets/animations/saas.json'
import mediaAnimation from '../../assets/animations/media.json'
import iotAnimation from '../../assets/animations/iot.json'
import secureCodeAnimation from '../../assets/animations/secure-code.json'
import paymentCardAnimation from '../../assets/animations/payment-card.json'
import marketplaceAnimation from '../../assets/animations/marketplace.json'
import serverAnimation from '../../assets/animations/server.json'
import microservicesAnimation from '../../assets/animations/microservices.json'
import maintenanceAnimation from '../../assets/animations/maintenance.json'
import databaseAnimation from '../../assets/animations/database.json'
import enterpriseAnimation from '../../assets/animations/enterprise.json'
import networkSecurityAnimation from '../../assets/animations/network-security.json'
import apiAnimation from '../../assets/animations/api.json'
import techStackAnimation from '../../assets/animations/tech-stack.json'
import growthAnimation from '../../assets/animations/growth.json'
import rocketAnimation from '../../assets/animations/rocket.json'

// Phases 2 and 3: the regional pages and the blog posts.
import financeAnimation from '../../assets/animations/finance.json'
import healthcareAnimation from '../../assets/animations/healthcare.json'
import governmentAnimation from '../../assets/animations/government.json'
import realEstateAnimation from '../../assets/animations/real-estate.json'
import energyAnimation from '../../assets/animations/energy.json'
import tourismAnimation from '../../assets/animations/tourism.json'
import educationAnimation from '../../assets/animations/education.json'
import languageAnimation from '../../assets/animations/language.json'
import complianceAnimation from '../../assets/animations/compliance.json'
import certificateAnimation from '../../assets/animations/certificate.json'
import currencyAnimation from '../../assets/animations/currency.json'
import payrollAnimation from '../../assets/animations/payroll.json'
import supplyChainAnimation from '../../assets/animations/supply-chain.json'
import hrAnimation from '../../assets/animations/hr.json'
import designAnimation from '../../assets/animations/design.json'
import documentationAnimation from '../../assets/animations/documentation.json'
import portfolioAnimation from '../../assets/animations/portfolio.json'
import communicationAnimation from '../../assets/animations/communication.json'
import customerExperienceAnimation from '../../assets/animations/customer-experience.json'
import lowCodeAnimation from '../../assets/animations/low-code.json'
import decommissionAnimation from '../../assets/animations/decommission.json'

/*
 * Geofence.json is reached through createLottieIcon rather than the existing
 * GeofenceIcon component. That component hardcodes style={{ width: size, height: size }}
 * with a 24px default, and an inline style beats the className="w-full h-full" the pages
 * pass, so it would render 24px inside a 56px slot. Going through createLottieIcon sizes
 * correctly and supports darkTreatment, and leaves the component and its one existing
 * consumer alone.
 */
import geofenceAnimation from '../../assets/animations/Geofence.json'

/**
 * The single place that decides which icon means what (DMX-045).
 *
 * Before this existed, every page declared its icons inline, which is exactly how four
 * components ended up standing in for about fifty different labels: nothing anywhere
 * showed you that an icon was already taken. A page now names a CONCEPT and the concept
 * resolves to one icon, so reuse across pages is a visible decision and reuse within a
 * page is a mistake the guard test catches.
 *
 * Adding a page: pick a concept from this list. If nothing fits, add a concept here
 * first rather than reaching for the nearest component. That ordering is the whole point.
 *
 * Provenance and licences for every asset: src/assets/ICON-SOURCES.md
 */
export const ICONS = {
  api: createLottieIcon(apiAnimation, 'ApiIcon'),
  mobile: AppStoreIcon,
  ecommerce: createLottieIcon(cartAnimation, 'CartIcon'),
  monitoring: createLottieIcon(monitoringAnimation, 'MonitoringIcon'),
  migration: createLottieIcon(dataTransferAnimation, 'DataTransferIcon'),
  database: createLottieIcon(databaseAnimation, 'DatabaseIcon'),
  infrastructureAsCode: DevelopmentIcon,
  enterprise: createLottieIcon(enterpriseAnimation, 'EnterpriseIcon'),
  security: FirewallIcon,
  growth: createLottieIcon(growthAnimation, 'GrowthIcon'),
  techStack: createLottieIcon(techStackAnimation, 'TechStackIcon'),
  managedService: createLottieIcon(maintenanceAnimation, 'MaintenanceIcon'),
  marketplace: createLottieIcon(marketplaceAnimation, 'MarketplaceIcon'),
  microservices: createLottieIcon(microservicesAnimation, 'MicroservicesIcon'),
  costOptimisation: createLottieIcon(optimizationAnimation, 'OptimizationIcon'),
  roadmap: createLottieIcon(roadmapAnimation, 'RoadmapIcon'),
  launch: createLottieIcon(rocketAnimation, 'RocketIcon'),
  secureCode: createLottieIcon(secureCodeAnimation, 'SecureCodeIcon'),
  iot: createLottieIcon(iotAnimation, 'IotIcon'),
  serverInfrastructure: createLottieIcon(serverAnimation, 'ServerIcon'),
  saas: createLottieIcon(saasAnimation, 'SaasIcon'),
  architecture: createLottieIcon(blueprintAnimation, 'BlueprintIcon'),
  networkSecurity: createLottieIcon(networkSecurityAnimation, 'NetworkSecurityIcon'),
  media: createLottieIcon(mediaAnimation, 'MediaIcon'),
  cicdPipeline: createLottieIcon(workflowAnimation, 'WorkflowIcon'),
  ai: AIIcon,
  analytics: AnalyticsIcon,
  cloud: CloudIcon,
  mvp: TargetIcon,
  payment: createLottieIcon(paymentCardAnimation, 'PaymentCardIcon'),
  enterpriseSoftware: CrmIcon,
  webApplications: WebDevelopmentIcon,
  consulting: ConsultingIcon,
  kubernetes: KubernetesLogo,
  ideaStage: LightbulbIcon,
  person: MaleAvatarIcon,
  process: ProcessIcon,
  support: SupportIcon,
  seedStage: StartupIcon,
  partnership: PartnershipIcon,

  // Industry sectors, for the two regional pages and the transformation post.
  finance: createLottieIcon(financeAnimation, 'FinanceIcon'),
  healthcare: createLottieIcon(healthcareAnimation, 'HealthcareIcon'),
  government: createLottieIcon(governmentAnimation, 'GovernmentIcon'),
  realEstate: createLottieIcon(realEstateAnimation, 'RealEstateIcon'),
  energy: createLottieIcon(energyAnimation, 'EnergyIcon'),
  tourism: createLottieIcon(tourismAnimation, 'TourismIcon'),
  education: createLottieIcon(educationAnimation, 'EducationIcon'),

  // The UAE specifics the enterprise software post covers.
  language: createLottieIcon(languageAnimation, 'LanguageIcon'),
  compliance: createLottieIcon(complianceAnimation, 'ComplianceIcon'),
  certificate: createLottieIcon(certificateAnimation, 'CertificateIcon'),
  currency: createLottieIcon(currencyAnimation, 'CurrencyIcon'),
  payroll: createLottieIcon(payrollAnimation, 'PayrollIcon'),
  supplyChain: createLottieIcon(supplyChainAnimation, 'SupplyChainIcon'),
  hr: createLottieIcon(hrAnimation, 'HrIcon'),

  // Delivery and process, for the remaining blog posts.
  design: createLottieIcon(designAnimation, 'DesignIcon'),
  documentation: createLottieIcon(documentationAnimation, 'DocumentationIcon'),
  portfolio: createLottieIcon(portfolioAnimation, 'PortfolioIcon'),
  communication: createLottieIcon(communicationAnimation, 'CommunicationIcon'),
  customerExperience: createLottieIcon(customerExperienceAnimation, 'CustomerExperienceIcon'),
  lowCode: createLottieIcon(lowCodeAnimation, 'LowCodeIcon'),
  decommission: createLottieIcon(decommissionAnimation, 'DecommissionIcon'),
  blockchain: Web3Icon,
  location: createLottieIcon(geofenceAnimation, 'LocationIcon'),
} satisfies Record<string, ComponentType<{ className?: string; darkTreatment?: 'silhouette' | 'none' }>>

/**
 * The second half of the Mobile concept. Mobile names two platforms, so the card shows
 * both in turn through AlternatingIcons, the same way React and Next.js do on About.
 */
export const MOBILE_SECOND = AndroidIcon
